import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Normalises a list of option keys into a comparable signature.
 *
 * Selection is a *set*: order and casing must not matter, so both the checked
 * boxes and a result's `data-keys` collapse to the same sorted, lowercased string.
 *
 * @param {string[]} keys
 * @returns {string}
 */
const NUMBER_PATTERN = /[\d.,]+/;

function signature(keys) {
  const cleaned = keys.map((key) => key.trim().toLowerCase()).filter(Boolean);

  return [...new Set(cleaned)].sort().join('|');
}

/**
 * Maps a set of checked options onto one result.
 *
 * Every result is pre-rendered by Liquid (price, product form and buy button stay
 * live Shopify data) and only toggled here, so no cart logic lives in this file.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement[]} checkboxes
 * @property {HTMLElement[]} [results]
 * @property {HTMLElement} [emptyState]
 * @property {HTMLElement} [noMatch]
 * @property {HTMLElement} [guarantee]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<Refs>}
 */
class TestSelectorComponent extends Component {
  requiredRefs = ['checkboxes'];

  /** First tick per page view, for the "selector used" baseline. */
  #started = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #trackTimer;

  connectedCallback() {
    super.connectedCallback();
    for (const result of this.refs.results ?? []) scaleAddonCounts(result);
    // Browsers restore checkbox state on back/forward navigation, so the panel
    // has to be reconciled once on connect and not only on change.
    this.#update();

    const fullListTrigger = document.querySelector(`[data-full-list-trigger="${this.id}"]`);
    if (fullListTrigger instanceof HTMLElement) fullListTrigger.hidden = !this.#fullList;
  }

  /** The test-funktioniert panel whose expand-all button lists every tested substance. */
  get #fullList() {
    return document.querySelector('test-funktioniert-component');
  }

  /**
   * Footer button (outside this element, addressed by id): opens the full list
   * via its own expand-all button, then scrolls to it and moves focus into it.
   */
  showFullList() {
    const fullList = this.#fullList;
    if (!(fullList instanceof HTMLElement)) return;

    const expandAll = fullList.querySelector('.test-search__expand-all');
    if (expandAll instanceof HTMLButtonElement && expandAll.dataset.state !== 'open') expandAll.click();

    (fullList.closest('.test-search') ?? fullList).scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    fullList.querySelector('summary')?.focus({ preventScroll: true });
  }

  /**
   * @param {Event} event - Change event from a single option checkbox.
   */
  handleChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const selection = this.#update();
    this.#announce();
    this.#track(selection);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#trackTimer);
  }

  /**
   * PostHog: `pdp_test_selector_started` once per page view, then
   * `pdp_test_selector_changed` for the settled selection (debounced, so
   * ticking through several boxes counts once). Not called on connect, so a
   * restored back/forward selection isn't reported.
   *
   * @param {{ baseKeys: string[], addonKeys: string[], matched: HTMLElement | null }} selection
   */
  #track({ baseKeys, addonKeys, matched }) {
    if (typeof window.phCapture !== 'function') return;

    if (!this.#started) {
      this.#started = true;
      window.phCapture('pdp_test_selector_started', {});
    }

    clearTimeout(this.#trackTimer);
    this.#trackTimer = setTimeout(() => {
      if (!baseKeys.length && !addonKeys.length) return;

      window.phCapture?.('pdp_test_selector_changed', {
        options: baseKeys,
        addons: addonKeys,
        recommended_test: matched?.dataset.productHandle ?? null,
        no_match: matched === null,
      });
    }, 800);
  }

  /**
   * Writes the outcome into the status region. Not called on connect, so a
   * restored back/forward selection isn't announced unprompted.
   */
  #announce() {
    const { status, noMatch } = this.refs;
    if (!status) return;

    const matched = (this.refs.results ?? []).find((result) => !result.hidden);

    if (matched) {
      status.textContent = matched.dataset.announcement ?? '';
    } else if (noMatch && !noMatch.hidden) {
      status.textContent = noMatch.innerText.trim();
    } else {
      status.textContent = '';
    }
  }

  #update() {
    const checked = this.refs.checkboxes.filter((input) => input.checked);
    const baseKeys = checked.filter((input) => input.dataset.addon !== 'true').map((input) => input.dataset.key ?? '');
    const addonKeys = checked.filter((input) => input.dataset.addon === 'true').map((input) => input.dataset.key ?? '');
    const results = this.refs.results ?? [];

    const matched = addonKeys.length
      ? this.#matchWithAddons(results, baseKeys, addonKeys)
      : this.#matchExact(results, baseKeys);

    for (const result of results) {
      result.hidden = result !== matched;
      this.#syncAddons(result, result === matched ? addonKeys : []);
    }

    const { emptyState, noMatch, guarantee } = this.refs;
    const anyChecked = checked.length > 0;

    if (emptyState) emptyState.hidden = anyChecked;
    if (noMatch) noMatch.hidden = !anyChecked || matched !== null;
    if (guarantee) guarantee.hidden = !anyChecked;

    return { baseKeys, addonKeys, matched };
  }

  /**
   * @param {HTMLElement[]} results
   * @param {string[]} baseKeys
   * @returns {HTMLElement | null} The result whose keys equal the selection.
   */
  #matchExact(results, baseKeys) {
    const selected = signature(baseKeys);
    if (selected === '') return null;

    return results.find((result) => signature(resultKeys(result)) === selected) ?? null;
  }

  /**
   * Add-on tests only exist next to some tests: each result lists the add-on
   * keys it can take (data-addon-keys). Among the results that take every
   * ticked add-on, the smallest one covering the other ticked options wins;
   * with only add-ons ticked that is the smallest such result.
   *
   * @param {HTMLElement[]} results
   * @param {string[]} baseKeys
   * @param {string[]} addonKeys
   * @returns {HTMLElement | null}
   */
  #matchWithAddons(results, baseKeys, addonKeys) {
    const wanted = signature(baseKeys).split('|').filter(Boolean);
    const wantedAddons = signature(addonKeys).split('|').filter(Boolean);
    let best = null;
    let bestSize = Infinity;

    for (const result of results) {
      const offered = signature((result.dataset.addonKeys ?? '').split(',')).split('|');
      if (!wantedAddons.every((key) => offered.includes(key))) continue;

      const keys = signature(resultKeys(result)).split('|').filter(Boolean);
      if (!wanted.every((key) => keys.includes(key))) continue;

      if (keys.length < bestSize) {
        best = result;
        bestSize = keys.length;
      }
    }

    return best;
  }

  /**
   * Shows the ticked add-ons in the result card, ticks their hidden form
   * checkboxes (product-form.js adds each as its own line) and updates the price.
   *
   * @param {HTMLElement} result
   * @param {string[]} addonKeys
   */
  #syncAddons(result, addonKeys) {
    const wanted = new Set(addonKeys.map((key) => key.trim().toLowerCase()));
    const isWanted = (/** @type {HTMLElement} */ el) => wanted.has((el.dataset.addonKey ?? '').toLowerCase());

    let extra = 0;
    let extraCount = 0;
    let shown = 0;
    for (const row of result.querySelectorAll('li[data-addon-key]')) {
      if (!(row instanceof HTMLElement)) continue;
      row.hidden = !isWanted(row);
      if (!row.hidden) {
        extra += Number(row.dataset.price) || 0;
        extraCount += Number(row.dataset.count) || 0;
        shown += 1;
      }
    }

    // A ticked add-on leaves "Optional dazu"; the list hides once it is empty.
    for (const row of result.querySelectorAll('li[data-addon-optional]')) {
      if (row instanceof HTMLElement) row.hidden = wanted.has((row.dataset.addonOptional ?? '').toLowerCase());
    }
    const optionalGroup = result.querySelector('[data-optional-group]');
    if (optionalGroup instanceof HTMLElement) {
      optionalGroup.hidden = !optionalGroup.querySelector('li:not([hidden])');
    }

    this.#syncSubstances(result, extraCount);

    for (const input of result.querySelectorAll('input[data-addon-key]')) {
      if (input instanceof HTMLInputElement) input.checked = isWanted(input);
    }

    const group = result.querySelector('[data-addons-group]');
    if (group instanceof HTMLElement) group.hidden = shown === 0;

    const price = result.querySelector('[data-base-price]');
    if (price instanceof HTMLElement) {
      price.dataset.baseText ??= price.textContent ?? '';
      const baseText = price.dataset.baseText;
      const total = (Number(price.dataset.basePrice) || 0) + extra;
      const separator = baseText.includes(',') ? ',' : '.';
      price.textContent = baseText.replace(NUMBER_PATTERN, (total / 100).toFixed(2).replace('.', separator));
    }
  }

  /**
   * Adds the ticked add-ons' substances to the count line and progress bar.
   *
   * @param {HTMLElement} result
   * @param {number} extraCount
   */
  #syncSubstances(result, extraCount) {
    const base = Number(result.dataset.baseCount) || 0;
    const total = Number(result.dataset.total) || 0;
    const count = base + extraCount;

    const countEl = result.querySelector('.test-selector__substances-count');
    if (countEl) countEl.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined).format(count);

    if (total > 0) {
      const progress = Math.min(100, Math.round((count / total) * 1000) / 10);
      result.style.setProperty('--test-selector-progress', `${progress}%`);
    }
  }
}

/**
 * With data-max-count, the add-on counts are scaled so that all offered add-ons
 * together reach exactly max − base (largest-remainder rounding keeps the sum
 * exact). Rewrites the chosen rows' data-count and the "+N" optional labels.
 *
 * @param {HTMLElement} result
 */
function scaleAddonCounts(result) {
  const max = Number(result.dataset.maxCount) || 0;
  const base = Number(result.dataset.baseCount) || 0;
  if (max <= base) return;

  const chosen = /** @type {HTMLElement[]} */ ([...result.querySelectorAll('li[data-addon-key]')]);
  const raw = chosen.map((row) => Number(row.dataset.count) || 0);
  const sum = raw.reduce((total, count) => total + count, 0);
  if (sum === 0) return;

  const target = max - base;
  const exact = raw.map((count) => (count * target) / sum);
  const scaled = exact.map(Math.floor);
  let left = target - scaled.reduce((total, count) => total + count, 0);
  const byRemainder = exact.map((value, index) => [value - Math.floor(value), index]).sort((a, b) => b[0] - a[0]);
  for (const [, index] of byRemainder) {
    if (left <= 0) break;
    scaled[index] += 1;
    left -= 1;
  }

  chosen.forEach((row, index) => {
    row.dataset.count = String(scaled[index]);
    const optional = result.querySelector(`li[data-addon-optional="${CSS.escape(row.dataset.addonKey ?? '')}"] .test-selector__optional-value`);
    if (optional) optional.textContent = (optional.textContent ?? '').replace(/\d+/, String(scaled[index]));
  });
}

/**
 * @param {HTMLElement} result
 * @returns {string[]}
 */
function resultKeys(result) {
  return (result.dataset.keys ?? '').split(',');
}

if (!customElements.get('test-selector-component')) {
  customElements.define('test-selector-component', TestSelectorComponent);
}
